import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Center,
  Loader,
  Paper,
  Stack,
  Text,
  Group,
  Button,
  Modal,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertCircle } from "@tabler/icons-react";
import {
  CollaborationChatDrawer,
  CollaborationChatFab,
} from "../CollaborationChatDrawer";

import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { asCollaborationRoleString, tryGetQuestionnaireId, type QuestionnaireId } from "@/utils/questionnaireMapping";
import { isQuestionnaireId } from "@/data/questionnaires/questionnaireRegistry";
import type {
  Question,
  Answer,
  MultiSelectAnswer,
  ScaleAnswer,
  PolicyAnswer,
  FullGradingResult,
  Questionnaire,
} from "@/utils/gradingEngine.types";
import { gradePairedQuestionnaires, type QuestionPairing } from "@/utils/gradingEngine";

import AssessmentSectionsNav, { type SectionStat } from "./AssessmentSectionsNav";
import AssessmentSectionCard from "./AssessmentSectionCard";
import AssessmentProgressPanel from "./AssessmentProgressPanel";
import AssessmentChatSidebar from "./AssessmentChatSidebar";
import AssessmentResultsView from "./AssessmentResultsView";
import { ASSESSMENT_SECTION_COLLAPSE_MS } from "./assessmentSection.const";

function isAnswered(question: Question, answer: Answer | undefined): boolean {
  if (!answer) return false;
  if (question.type === "multi_select") {
    return ((answer as MultiSelectAnswer).selectedOptionIds?.length ?? 0) > 0;
  }
  return !!(answer as ScaleAnswer | PolicyAnswer).selectedOptionId;
}

type QuestionnaireResponseData = {
  questionnaireId: string | null;
  myQuestionnaire: Questionnaire | null;
  requesterQuestionnaire: Questionnaire | null;
  receiverQuestionnaire: Questionnaire | null;
  questionPairings: QuestionPairing[] | null;
  aiReady: boolean;
  aiError: string | null;
  myAnswers: Record<string, Answer> | null;
  mySubmittedAt: string | null;
  partnerSubmittedAt: string | null;
  bothSubmitted: boolean;
  partnerAnswers: Record<string, Answer> | null;
  gradingResult: unknown | null;
};

type Props = {
  matchId: string;
  collaboration: {
    id: string;
    requesterRole?: string | null;
    expectedPartnerRole?: string | null;
    partnerReadinessQuestionnaireId?: string | null;
    expressInterestTitle?: string | null;
    expressInterestSummary?: string | null;
    requesterCompanyId: string;
    receiverCompanyId: string;
  };
  userCompanyId: string;
  currentUserId: string | undefined;
  /** When false, chat & socket disabled and a "preview locked" UI is shown. */
  chatOpen: boolean;
  /** True when collaboration period has ended; everything becomes read-only. */
  collaborationReadOnly?: boolean;
  /** When true, the workspace is locked (pre-payment). Chat shows preview lock; questionnaire still renders but read-only. */
  workspaceLocked?: boolean;
  /** If the assessment URL ?tab=assessment was the entry, optionally auto-focus first unanswered. */
  autoFocusFirstUnanswered?: boolean;
};

export default function AssessmentTab({
  matchId,
  collaboration,
  userCompanyId,
  currentUserId,
  chatOpen,
  collaborationReadOnly = false,
  workspaceLocked = false,
  autoFocusFirstUnanswered = false,
}: Props) {
  const isLgUp = useMediaQuery("(min-width: 1280px)", false, { getInitialValueInEffect: true });
  const isMdUp = useMediaQuery("(min-width: 900px)", false, { getInitialValueInEffect: true });

  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [loading, setLoading] = useState(true);
  const [responseData, setResponseData] = useState<QuestionnaireResponseData | null>(null);
  const [gradingResult, setGradingResult] = useState<FullGradingResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState(0);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const gradingTriggered = useRef(false);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const middleScrollRef = useRef<HTMLDivElement | null>(null);

  const middleScrollStyle = useMemo(
    () =>
      ({
        overflowY: "auto",
        minHeight: 0,
        paddingRight: 4,
        overflowAnchor: "none",
      }) as const,
    []
  );

  const questionnaire = useMemo<Questionnaire | null>(() => {
    const q = responseData?.myQuestionnaire;
    if (q?.sections?.length) return q;
    return null;
  }, [responseData?.myQuestionnaire]);

  const resolvedQuestionnaireId = questionnaire?.id ?? responseData?.questionnaireId ?? null;

  // ── Fetch responses ──────────────────────────────────────────────────────
  const fetchResponses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await trpc.collaboration.getQuestionnaireResponses.query({ matchId });
      setResponseData(data as QuestionnaireResponseData);
      if (data.myAnswers) {
        setAnswers(data.myAnswers as unknown as Record<string, Answer>);
      }
    } catch (err: any) {
      const code = err?.data?.code ?? err?.shape?.data?.code;
      // If collaboration was archived/deleted (e.g. re-request after decline), treat as empty instead of surfacing an error toast.
      if (code === "NOT_FOUND") {
        setResponseData(null);
      } else {
        handleTrpcError(err);
      }
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  // ── Grade when both submitted ────────────────────────────────────────────
  useEffect(() => {
    if (
      !responseData?.bothSubmitted ||
      !responseData?.myAnswers ||
      !responseData?.partnerAnswers ||
      !questionnaire ||
      gradingTriggered.current
    ) {
      return;
    }
    if (responseData.gradingResult) {
      setGradingResult(responseData.gradingResult as FullGradingResult);
      return;
    }
    gradingTriggered.current = true;
    const isRequester = collaboration.requesterCompanyId === userCompanyId;
    const reqQ = responseData.requesterQuestionnaire;
    const recQ = responseData.receiverQuestionnaire;
    const pairings = responseData.questionPairings;
    const reqAnswers = (isRequester
      ? responseData.myAnswers
      : responseData.partnerAnswers) as Record<string, Answer>;
    const recAnswers = (isRequester
      ? responseData.partnerAnswers
      : responseData.myAnswers) as Record<string, Answer>;

    try {
      const result =
        reqQ && recQ && pairings?.length
          ? gradePairedQuestionnaires(reqQ, recQ, pairings, reqAnswers, recAnswers)
          : null;
      if (!result) return;
      setGradingResult(result);
      if (!collaborationReadOnly) {
        trpc.collaboration.saveGradingResult
          .mutate({ matchId, gradingResult: result as unknown as Record<string, unknown> })
          .catch((err) => console.error("Failed to save grading result:", err));
      }
    } catch (err) {
      console.error("Grading engine error:", err);
    }
  }, [
    responseData,
    questionnaire,
    matchId,
    collaboration.requesterCompanyId,
    userCompanyId,
    collaborationReadOnly,
  ]);

  // ── Flatten sections to question list ────────────────────────────────────
  const sections = useMemo(() => questionnaire?.sections ?? [], [questionnaire]);
  const flatQuestions = useMemo(() => {
    const list: Array<{ q: Question; sectionId: string; sectionTitle: string; sectionIndex: number }> = [];
    sections.forEach((s, sIdx) => {
      s.questions.forEach((q) => list.push({ q, sectionId: s.id, sectionTitle: s.title, sectionIndex: sIdx }));
    });
    return list;
  }, [sections]);

  const totalQuestionCount = flatQuestions.length;

  // ── Auto-focus first section with unanswered question on first load ──────
  const didInitialFocus = useRef(false);
  useEffect(() => {
    if (didInitialFocus.current) return;
    if (loading || !responseData || sections.length === 0) return;
    if (!autoFocusFirstUnanswered && (responseData.myAnswers ?? Object.keys(answers).length) === 0) {
      didInitialFocus.current = true;
      setExpandedSectionIndex(0);
      return;
    }
    const firstUnansweredSection = sections.findIndex((section) =>
      section.questions.some((q) => !isAnswered(q, answers[q.id]))
    );
    setExpandedSectionIndex(firstUnansweredSection === -1 ? 0 : firstUnansweredSection);
    didInitialFocus.current = true;
  }, [loading, responseData, sections, answers, autoFocusFirstUnanswered]);

  // ── Derived stats ────────────────────────────────────────────────────────
  const answeredCount = useMemo(
    () => flatQuestions.reduce((n, { q }) => (isAnswered(q, answers[q.id]) ? n + 1 : n), 0),
    [flatQuestions, answers]
  );
  const remainingCount = Math.max(0, totalQuestionCount - answeredCount);
  const percentComplete =
    totalQuestionCount > 0 ? Math.round((answeredCount / totalQuestionCount) * 100) : 0;
  const sectionStats: SectionStat[] = useMemo(() => {
    return sections.map((section, idx) => {
      const total = section.questions.length;
      const done = section.questions.filter((q) => isAnswered(q, answers[q.id])).length;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      return {
        id: section.id,
        title: section.title,
        percent: pct,
        active: expandedSectionIndex >= 0 && expandedSectionIndex === idx,
        completed: total > 0 && done === total,
      };
    });
  }, [sections, answers, expandedSectionIndex]);

  const requiredQuestionIds = useMemo(
    () => flatQuestions.filter(({ q }) => q.required).map(({ q }) => q.id),
    [flatQuestions]
  );
  const requiredSatisfied = useMemo(
    () =>
      requiredQuestionIds.length === 0 ||
      requiredQuestionIds.every((id) => {
        const entry = flatQuestions.find((e) => e.q.id === id);
        return entry ? isAnswered(entry.q, answers[id]) : false;
      }),
    [requiredQuestionIds, flatQuestions, answers]
  );
  const allAnswered = answeredCount === totalQuestionCount && totalQuestionCount > 0;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleAnswerChange = useCallback((answer: Answer) => {
    setAnswers((prev) => ({ ...prev, [answer.questionId]: answer }));
  }, []);

  const handleClearQuestion = useCallback((questionId: string) => {
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });
  }, []);

  const scrollSectionInColumn = useCallback((sectionId: string, delayMs = 0) => {
    window.setTimeout(() => {
      const container = middleScrollRef.current;
      const el = sectionRefs.current[sectionId];
      if (!container || !el) return;
      const top = el.offsetTop - container.offsetTop - 8;
      container.scrollTo({ top: Math.max(0, top), behavior: "auto" });
    }, delayMs);
  }, []);

  const openSection = useCallback(
    (sectionIndex: number) => {
      if (sectionIndex < 0 || sectionIndex >= sections.length) return;
      setExpandedSectionIndex(sectionIndex);
      const sectionId = sections[sectionIndex]?.id;
      if (sectionId) scrollSectionInColumn(sectionId, 80);
    },
    [sections, scrollSectionInColumn]
  );

  /** Close current, open next; keep the collapsed previous card visible at the top. */
  const goToNextSection = useCallback(
    (fromIndex: number) => {
      const nextIndex = fromIndex + 1;
      if (nextIndex >= sections.length) return;
      setExpandedSectionIndex(nextIndex);
      const prevSectionId = sections[fromIndex]?.id;
      if (prevSectionId) {
        scrollSectionInColumn(prevSectionId, ASSESSMENT_SECTION_COLLAPSE_MS + 40);
      }
    },
    [sections, scrollSectionInColumn]
  );

  const handleSectionOpenedChange = useCallback((sectionIndex: number, opened: boolean) => {
    if (opened) {
      setExpandedSectionIndex(sectionIndex);
      return;
    }
    setExpandedSectionIndex((current) => (current === sectionIndex ? -1 : current));
  }, []);

  const handleSelectSection = useCallback(
    (sectionIndex: number) => {
      openSection(sectionIndex);
    },
    [openSection]
  );

  const performSubmit = useCallback(async () => {
    if (collaborationReadOnly || workspaceLocked || !questionnaire || !requiredSatisfied || !resolvedQuestionnaireId) return;
    setSubmitConfirmOpen(false);
    setSubmitting(true);
    try {
      await trpc.collaboration.submitQuestionnaire.mutate({
        matchId,
        questionnaireId: resolvedQuestionnaireId,
        answers,
      });
      successNotification({
        title: "Assessment Submitted",
        message: "Your responses have been recorded successfully.",
      });
      await fetchResponses();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSubmitting(false);
    }
  }, [
    collaborationReadOnly,
    workspaceLocked,
    questionnaire,
    requiredSatisfied,
    resolvedQuestionnaireId,
    matchId,
    answers,
    fetchResponses,
  ]);

  const requestSubmit = useCallback(() => {
    if (collaborationReadOnly || workspaceLocked || !questionnaire || !requiredSatisfied || !resolvedQuestionnaireId) return;
    if (allAnswered) {
      void performSubmit();
    } else {
      setSubmitConfirmOpen(true);
    }
  }, [
    collaborationReadOnly,
    workspaceLocked,
    questionnaire,
    requiredSatisfied,
    resolvedQuestionnaireId,
    allAnswered,
    performSubmit,
  ]);

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Center mih="50vh">
        <Loader size="lg" color="primary" />
      </Center>
    );
  }

  // Stale collaboration object after route change
  if (collaboration.id !== matchId) {
    return (
      <Center mih="50vh">
        <Loader size="lg" color="primary" />
      </Center>
    );
  }

  // ── No questionnaire resolvable ──────────────────────────────────────────
  if (!questionnaire) {
    const hasBothRoles = !!(
      asCollaborationRoleString(collaboration.requesterRole) &&
      asCollaborationRoleString(collaboration.expectedPartnerRole)
    );
    const emptyDetail = responseData?.aiError
      ? `We could not prepare your personalized assessment: ${responseData.aiError}`
      : hasBothRoles
        ? "Your personalized assessment is being prepared. Refresh in a moment or contact support if this persists."
        : "Collaboration roles have not been assigned yet. The assessment will appear once both parties' roles are determined.";
    return (
      <Center mih="50vh" px="md">
        <Paper
          withBorder
          radius="md"
          p="xl"
          maw={480}
          style={{ backgroundColor: "#ffffff", borderColor: "var(--mantine-color-slate-2)" }}
        >
          <Stack align="center" gap="md">
            <IconAlertCircle size={48} color="var(--mantine-color-primary-6)" stroke={1.5} />
            <Text fw={700} fz="lg" c="primary.8" ta="center">
              No assessment available
            </Text>
            <Text fz="sm" c="slate.6" ta="center" lh={1.55}>
              {emptyDetail}
            </Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  // ── State variables ──────────────────────────────────────────────────────
  const isSubmitted = !!responseData?.mySubmittedAt;
  const partnerSubmitted = !!responseData?.partnerSubmittedAt;
  const bothSubmitted = !!responseData?.bothSubmitted;
  const effectiveReadOnly = collaborationReadOnly || workspaceLocked || isSubmitted;

  const assessmentQuestionnaireId: QuestionnaireId | null = (() => {
    const candidate = resolvedQuestionnaireId ?? questionnaire.id;
    if (isQuestionnaireId(candidate)) return candidate;
    return tryGetQuestionnaireId(collaboration.requesterRole, collaboration.expectedPartnerRole);
  })();

  const renderSectionAccordion = (readOnly: boolean) => (
    <Stack gap={0}>
      {sections.map((section, idx) => {
        const sectionAnswered = section.questions.filter((q) => isAnswered(q, answers[q.id])).length;
        return (
          <AssessmentSectionCard
            key={section.id}
            ref={(el) => {
              sectionRefs.current[section.id] = el;
            }}
            section={section}
            sectionIndex={idx}
            questions={section.questions}
            answers={answers}
            opened={expandedSectionIndex === idx}
            onOpenedChange={(opened) => handleSectionOpenedChange(idx, opened)}
            answeredCount={sectionAnswered}
            isLastSection={idx === sections.length - 1}
            readOnly={readOnly}
            submitting={submitting}
            canSubmit={requiredSatisfied}
            onChange={handleAnswerChange}
            onClear={handleClearQuestion}
            onNext={() => goToNextSection(idx)}
            onSubmit={requestSubmit}
          />
        );
      })}
    </Stack>
  );

  // ── Middle column content based on state ─────────────────────────────────
  let middleContent: React.ReactNode;
  if (bothSubmitted && assessmentQuestionnaireId) {
    middleContent = (
      <AssessmentResultsView
        questionnaire={questionnaire}
        resolvedQuestionnaireId={assessmentQuestionnaireId}
        gradingResult={gradingResult}
        myAnswers={(responseData?.myAnswers as Record<string, Answer> | null) ?? {}}
        partnerAnswers={(responseData?.partnerAnswers as Record<string, Answer> | null) ?? {}}
      />
    );
  } else if (isSubmitted && !partnerSubmitted) {
    middleContent = (
      <Stack gap="md">
        <Paper
          withBorder
          radius="md"
          p="xl"
          style={{ backgroundColor: "#ffffff", borderColor: "var(--mantine-color-slate-2)" }}
        >
          <Center>
            <Stack align="center" gap="md">
              <Text fw={700} fz="lg" c="primary.8">
                Waiting for your partner
              </Text>
              <Text fz="sm" c="slate.6" ta="center" maw={420}>
                Your responses have been recorded. Compatibility results will appear here once your partner submits
                their assessment.
              </Text>
            </Stack>
          </Center>
        </Paper>
        {renderSectionAccordion(true)}
      </Stack>
    );
  } else {
    middleContent = renderSectionAccordion(effectiveReadOnly);
  }

  // ── Sections nav (left) ──────────────────────────────────────────────────
  const sectionsNav = (
    <Box style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflowY: "auto" }}>
      <AssessmentSectionsNav sections={sections} stats={sectionStats} onSelect={handleSelectSection} />
      {!bothSubmitted && (
        <AssessmentProgressPanel percent={percentComplete} total={totalQuestionCount} />
      )}
    </Box>
  );

  // ── Right column (chat only — fills full height) ─────────────────────────
  const rightColumn = (
    <Box style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <AssessmentChatSidebar
        matchId={matchId}
        chatOpen={chatOpen}
        collaborationReadOnly={collaborationReadOnly}
        currentUserId={currentUserId}
        previewLocked={workspaceLocked}
      />
    </Box>
  );

  // ── Layout ───────────────────────────────────────────────────────────────
  if (isLgUp) {
    return (
      <>
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "220px 1fr 360px",
            gap: 16,
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            style={{
              backgroundColor: "#fafbfc",
              borderRadius: 10,
              border: "1px solid var(--mantine-color-slate-2)",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {sectionsNav}
          </Box>
          <Box ref={middleScrollRef} style={middleScrollStyle}>
            {middleContent}
          </Box>
          <Box style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>{rightColumn}</Box>
        </Box>
        <SubmitConfirmModal
          opened={submitConfirmOpen}
          unansweredCount={remainingCount}
          submitting={submitting}
          onCancel={() => setSubmitConfirmOpen(false)}
          onConfirm={() => void performSubmit()}
        />
      </>
    );
  }

  if (isMdUp) {
    return (
      <>
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "200px 1fr",
            gap: 16,
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            style={{
              backgroundColor: "#fafbfc",
              borderRadius: 10,
              border: "1px solid var(--mantine-color-slate-2)",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {sectionsNav}
          </Box>
          <Box ref={middleScrollRef} style={middleScrollStyle}>
            {middleContent}
          </Box>
        </Box>
        <CollaborationChatDrawer
          opened={chatDrawerOpen}
          onClose={() => setChatDrawerOpen(false)}
          matchId={matchId}
          chatOpen={chatOpen}
          collaborationReadOnly={collaborationReadOnly}
          currentUserId={currentUserId}
          previewLocked={workspaceLocked}
          backAriaLabel="Back to assessment"
        />
        <CollaborationChatFab
          onClick={() => setChatDrawerOpen(true)}
          disabled={!chatOpen}
          locked={workspaceLocked}
        />
        <SubmitConfirmModal
          opened={submitConfirmOpen}
          unansweredCount={remainingCount}
          submitting={submitting}
          onCancel={() => setSubmitConfirmOpen(false)}
          onConfirm={() => void performSubmit()}
        />
      </>
    );
  }

  // Mobile: single column + chat drawer
  return (
    <>
      <Box ref={middleScrollRef} style={{ ...middleScrollStyle, flex: 1, paddingBottom: 64 }}>
        <Stack gap="md">
          <Paper
            withBorder
            radius="md"
            p="md"
            style={{ backgroundColor: "#ffffff", borderColor: "var(--mantine-color-slate-2)" }}
          >
            {sectionsNav}
          </Paper>
          {middleContent}
        </Stack>
      </Box>
      <CollaborationChatDrawer
        opened={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        matchId={matchId}
        chatOpen={chatOpen}
        collaborationReadOnly={collaborationReadOnly}
        currentUserId={currentUserId}
        previewLocked={workspaceLocked}
        backAriaLabel="Back to assessment"
      />
      <CollaborationChatFab onClick={() => setChatDrawerOpen(true)} disabled={!chatOpen} locked={workspaceLocked} />
      <SubmitConfirmModal
        opened={submitConfirmOpen}
        unansweredCount={remainingCount}
        submitting={submitting}
        onCancel={() => setSubmitConfirmOpen(false)}
        onConfirm={() => void performSubmit()}
      />
    </>
  );
}

function SubmitConfirmModal({
  opened,
  unansweredCount,
  submitting,
  onCancel,
  onConfirm,
}: {
  opened: boolean;
  unansweredCount: number;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={
        <Text fw={700} fz="md" c="primary.8">
          Unanswered questions
        </Text>
      }
      centered
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      <Text fz="sm" c="slate.6" mb="lg" lh={1.55}>
        You still have {unansweredCount} unanswered question{unansweredCount === 1 ? "" : "s"}. Do you want to submit
        anyway?
      </Text>
      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          loading={submitting}
          onClick={onConfirm}
          color="yellow"
          variant="filled"
          styles={{ root: { color: "var(--mantine-color-primary-7)", fontWeight: 700 } }}
        >
          Submit anyway
        </Button>
      </Group>
    </Modal>
  );
}
